import type { StoreRole } from "./auth";

/**
 * TCGMA-177 — route-level role enforcement.
 *
 * `RequireRole` only wraps in-page CONTROLS (a button, a section). Nothing
 * guarded whole ROUTES, so a Staff member could deep-link straight to
 * /tools/imports or /settings/user-management and the page would render, then
 * 403 field by field as each request failed — which reads as a broken app
 * rather than "you don't have access".
 *
 * The SERVER remains the real boundary: every endpoint is requireAuth +
 * requireRole gated (TNK-78), and this map cannot grant anyone anything. What it
 * does is stop the app rendering a page the user can't actually use, and make
 * the role matrix explicit in one place instead of scattered across tiles.
 *
 * Deliberately NOT a fix for a signed-in shared device: someone holding a valid
 * operator session passes every check here by definition. That's solved by not
 * letting the kiosk hold a session at all (ephemeral browser profile).
 *
 * Keep in sync with the `min:` values on the tiles in routes/tools.index.tsx and
 * routes/settings.index.tsx — those decide what's VISIBLE, this decides what's
 * REACHABLE, and they should agree.
 */

/** Path prefix → minimum role. Longest prefix wins (see requiredRoleFor). */
const ROUTE_ROLES: ReadonlyArray<readonly [string, StoreRole]> = [
  // ── Owner only ──
  ["/settings/user-management", "owner"], // members + invites are requireRole("owner")

  // ── Manager+ : the main operator sections ──
  // NAV_ITEMS (__root) already declares these Manager+, but that only HID them
  // from the nav — a Staff member could still deep-link straight in. Their APIs
  // are manager-gated end to end (sales/products/liveListings), so the page was
  // only ever going to 403 its way to a broken shell.
  ["/products", "manager"],
  ["/live", "manager"],
  ["/sales", "manager"],
  ["/reservations", "manager"],

  // ── Manager+ : tools & settings ──
  ["/tools/imports", "manager"],
  ["/tools/import", "manager"],
  ["/tools/listing-import", "manager"],
  ["/tools/scan", "manager"],
  ["/tools/supplies", "manager"],
  ["/tools/lot-analyzer", "manager"],
  ["/tools/pricetrack", "manager"],
  ["/tools/recommendations", "manager"],
  ["/tools/reports", "manager"],
  ["/tools/deck-templates", "manager"],
  ["/tools/deck-search", "manager"],
  ["/tools/bundle-templates", "manager"],
  ["/tools/bundle-template-edit", "manager"],
  ["/tools/bundle-settings", "manager"],
  ["/tools/costbasis-backfill", "manager"],
  ["/tools/leads", "manager"], // customer PII + email export
  // /settings/store-options was reachable by Staff even though GET+PUT
  // /store/profile/me are both requireRole("manager") — the page couldn't even
  // load for them.
  ["/settings/store-options", "manager"],
  ["/settings/ebay", "manager"],
  ["/settings/listing-templates", "manager"],
  ["/settings/bundle-defaults", "manager"],
];

/**
 * The minimum role needed for a path, or null when any signed-in member may go
 * there. Longest matching prefix wins, so a child route (/tools/imports/abc/review)
 * inherits its parent's requirement, while a sibling that merely shares a string
 * prefix (/tools/bundle-template-edit vs /tools/bundle-templates) does not —
 * hence the explicit "/" boundary check.
 */
export function requiredRoleFor(pathname: string): StoreRole | null {
  let bestPrefix = "";
  let bestRole: StoreRole | null = null;

  for (const [prefix, role] of ROUTE_ROLES) {
    const isMatch = pathname === prefix || pathname.startsWith(`${prefix}/`);
    if (isMatch && prefix.length > bestPrefix.length) {
      bestPrefix = prefix;
      bestRole = role;
    }
  }
  return bestRole;
}
